import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import app from "../firebase.js";

function Connexion() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth(app);

  // 🔍 Vérifie si l'utilisateur est déjà connecté
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        await currentUser.reload();

        if (currentUser.emailVerified) {
          setUser(currentUser);
          navigate("/education");
        } else {
          await signOut(auth);
          setUser(null);
        }
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // 🔐 Connexion utilisateur
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      await userCredential.user.reload();

      if (!userCredential.user.emailVerified) {
        await signOut(auth);
        setUser(null);
        setMessage(
          "⚠️ Votre adresse email n’est pas encore vérifiée. Veuillez cliquer sur le lien reçu par email avant de vous connecter."
        );
        return;
      }

      setUser(userCredential.user);
      setMessage("✅ Connexion réussie !");
      navigate("/education");
    } catch (error) {
      setMessage("❌ Erreur : identifiants incorrects");
    } finally {
      setIsLoading(false);
    }
  };

  // ✉️ Renvoyer l'email de vérification
  const handleResendVerification = async () => {
    if (!email || !password) {
      setMessage(
        "⚠️ Veuillez renseigner votre email et votre mot de passe avant de demander un nouvel email de vérification."
      );
      return;
    }

    setIsSendingVerification(true);
    setMessage("");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const currentUser = userCredential.user;

      await currentUser.reload();

      if (currentUser.emailVerified) {
        setMessage("✅ Votre adresse email est déjà vérifiée. Vous pouvez vous connecter.");
        return;
      }

      await sendEmailVerification(currentUser);
      await signOut(auth);

      setMessage(
        "📩 Un nouvel email de vérification vous a été envoyé. Vérifiez votre boîte mail puis reconnectez-vous."
      );
    } catch (error) {
      setMessage(
        "❌ Impossible d’envoyer l’email de vérification. Vérifiez vos identifiants."
      );
    } finally {
      setIsSendingVerification(false);
    }
  };

  // 🔑 Mot de passe oublié
  const handleForgotPassword = async () => {
    if (!email) {
      alert(
        "⚠️ Veuillez entrer votre adresse e-mail avant de cliquer sur 'Mot de passe oublié'."
      );
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert("📩 Un e-mail de réinitialisation a été envoyé !");
    } catch (error) {
      console.error(error);
      alert("❌ Erreur lors de l'envoi de l'e-mail.");
    }
  };

  // 🚪 Déconnexion
  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setMessage("🚪 Déconnecté !");
  };

  const showResendButton =
    message.includes("pas encore vérifiée") ||
    message.includes("vérification");

  return (
    <div style={styles.container}>
      <div style={styles.bgCircle1} />
      <div style={styles.bgCircle2} />

      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <span style={styles.icon}>🔐</span>
          </div>
          <h2 style={styles.title}>Connexion</h2>
          <p style={styles.subtitle}>Accédez à votre espace</p>
        </div>

        {!user ? (
          <form onSubmit={handleLogin} style={styles.form}>
            <div style={styles.inputGroup}>
              <div
                style={{
                  ...styles.inputWrapper,
                  ...(focusedInput === "email" ? styles.inputWrapperFocused : {}),
                }}
              >
                <span style={styles.inputIcon}>📧</span>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedInput("email")}
                  onBlur={() => setFocusedInput(null)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <div
                style={{
                  ...styles.inputWrapper,
                  ...(focusedInput === "password"
                    ? styles.inputWrapperFocused
                    : {}),
                }}
              >
                <span style={styles.inputIcon}>🔒</span>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedInput("password")}
                  onBlur={() => setFocusedInput(null)}
                  style={{
                    ...styles.input,
                    paddingRight: "40px",
                  }}
                  required
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    ...styles.eyeIcon,
                    transform:
                      hoveredButton === "eye" ? "scale(1.1)" : "scale(1)",
                  }}
                  onMouseEnter={() => setHoveredButton("eye")}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  {showPassword ? "🙈" : "👁️"}
                </span>
              </div>
            </div>

            <div
              style={{
                ...styles.forgotPassword,
                ...(hoveredButton === "forgot"
                  ? styles.forgotPasswordHover
                  : {}),
              }}
              onClick={handleForgotPassword}
              onMouseEnter={() => setHoveredButton("forgot")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Mot de passe oublié ?
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(hoveredButton === "login" ? styles.buttonHover : {}),
                opacity: isLoading ? 0.7 : 1,
              }}
              onMouseEnter={() => setHoveredButton("login")}
              onMouseLeave={() => setHoveredButton(null)}
              disabled={isLoading}
            >
              {isLoading ? (
                <div style={styles.loaderWrapper}>
                  <div style={styles.spinner} />
                  <span>Connexion...</span>
                </div>
              ) : (
                "Se connecter"
              )}
            </button>

            {showResendButton && (
              <button
                type="button"
                onClick={handleResendVerification}
                style={{
                  ...styles.verifyButton,
                  ...(hoveredButton === "verify" ? styles.verifyButtonHover : {}),
                  opacity: isSendingVerification ? 0.7 : 1,
                }}
                onMouseEnter={() => setHoveredButton("verify")}
                onMouseLeave={() => setHoveredButton(null)}
                disabled={isSendingVerification}
              >
                {isSendingVerification ? "Envoi..." : "Renvoyer l’email de vérification"}
              </button>
            )}

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>ou</span>
              <div style={styles.dividerLine} />
            </div>

            <button
              type="button"
              style={{
                ...styles.buttonSMS,
                ...(hoveredButton === "sms" ? styles.buttonSMSHover : {}),
              }}
              onMouseEnter={() => setHoveredButton("sms")}
              onMouseLeave={() => setHoveredButton(null)}
              onClick={() => (window.location.href = "/connexion-sms")}
            >
              <span style={styles.buttonIcon}>📱</span>
              Connexion par SMS
            </button>
          </form>
        ) : (
          <div style={styles.welcomeContainer}>
            <div style={styles.welcomeIcon}>👋</div>
            <p style={styles.welcomeEmail}>{user.email}</p>
            <button
              onClick={handleLogout}
              style={{
                ...styles.logout,
                ...(hoveredButton === "logout" ? styles.logoutHover : {}),
              }}
              onMouseEnter={() => setHoveredButton("logout")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Se déconnecter
            </button>
          </div>
        )}

        {message && (
          <div
            style={{
              ...styles.message,
              ...(message.includes("✅") || message.includes("📩")
                ? styles.messageSuccess
                : styles.messageError),
            }}
          >
            {message}
          </div>
        )}

        {!user && (
          <p style={styles.signupText}>
            Pas de compte ?{" "}
            <Link
              to="/inscription"
              style={{
                ...styles.link,
                ...(hoveredButton === "signup" ? styles.linkHover : {}),
              }}
              onMouseEnter={() => setHoveredButton("signup")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              Créez-en un
            </Link>
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: "16px",
    overflow: "hidden",
  },
  bgCircle1: {
    position: "absolute",
    width: "250px",
    height: "250px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.08)",
    top: "-60px",
    left: "-60px",
    filter: "blur(50px)",
    animation: "float 6s ease-in-out infinite",
  },
  bgCircle2: {
    position: "absolute",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.08)",
    bottom: "-40px",
    right: "-40px",
    filter: "blur(50px)",
    animation: "float 8s ease-in-out infinite",
  },
  card: {
    position: "relative",
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    padding: "24px 20px",
    width: "100%",
    maxWidth: "360px",
    zIndex: 1,
  },
  header: {
    textAlign: "center",
    marginBottom: "20px",
  },
  iconWrapper: {
    width: "52px",
    height: "52px",
    margin: "0 auto 12px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.35)",
    animation: "float 3s ease-in-out infinite",
  },
  icon: {
    fontSize: "26px",
  },
  title: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#1a202c",
    marginBottom: "4px",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: "13px",
    color: "#718096",
    fontWeight: "500",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    background: "#f7fafc",
    borderRadius: "12px",
    border: "2px solid #e2e8f0",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  inputWrapperFocused: {
    border: "2px solid #667eea",
    boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
    background: "#fff",
  },
  inputIcon: {
    position: "absolute",
    left: "12px",
    fontSize: "16px",
    zIndex: 1,
    transition: "transform 0.3s ease",
  },
  input: {
    padding: "11px 11px 11px 40px",
    borderRadius: "12px",
    border: "none",
    fontSize: "14px",
    width: "100%",
    transition: "all 0.3s ease",
    outline: "none",
    fontWeight: "500",
    color: "#2d3748",
    boxSizing: "border-box",
    background: "transparent",
  },
  eyeIcon: {
    position: "absolute",
    right: "12px",
    cursor: "pointer",
    fontSize: "18px",
    transition: "all 0.2s ease",
    userSelect: "none",
  },
  forgotPassword: {
    color: "#667eea",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "right",
    transition: "all 0.3s ease",
    marginTop: "-4px",
  },
  forgotPasswordHover: {
    color: "#764ba2",
    textDecoration: "underline",
  },
  button: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
    marginTop: "4px",
  },
  buttonHover: {
    transform: "translateY(-2px) scale(1.01)",
    boxShadow: "0 6px 25px rgba(102, 126, 234, 0.5)",
  },
  verifyButton: {
    background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
    color: "#fff",
    border: "none",
    padding: "11px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 15px rgba(245, 158, 11, 0.35)",
  },
  verifyButtonHover: {
    transform: "translateY(-2px) scale(1.01)",
    boxShadow: "0 6px 22px rgba(245, 158, 11, 0.45)",
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    margin: "8px 0",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
  },
  dividerText: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#a0aec0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  buttonSMS: {
    background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
    color: "#fff",
    border: "none",
    padding: "12px",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 15px rgba(72, 187, 120, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  buttonSMSHover: {
    transform: "translateY(-2px) scale(1.01)",
    boxShadow: "0 6px 25px rgba(72, 187, 120, 0.5)",
  },
  buttonIcon: {
    fontSize: "16px",
  },
  welcomeContainer: {
    textAlign: "center",
    padding: "12px 0",
  },
  welcomeIcon: {
    fontSize: "40px",
    marginBottom: "10px",
    animation: "float 2s ease-in-out infinite",
  },
  welcomeEmail: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#1a202c",
    marginBottom: "16px",
    wordBreak: "break-all",
  },
  logout: {
    marginTop: "8px",
    padding: "10px 24px",
    background: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "13px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 4px 15px rgba(245, 101, 101, 0.4)",
  },
  logoutHover: {
    transform: "translateY(-2px) scale(1.02)",
    boxShadow: "0 6px 25px rgba(245, 101, 101, 0.5)",
  },
  message: {
    marginTop: "14px",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    textAlign: "center",
    animation: "pulse 2s ease-in-out infinite",
  },
  messageSuccess: {
    background: "linear-gradient(135deg, #c6f6d5 0%, #9ae6b4 100%)",
    color: "#22543d",
    border: "2px solid #68d391",
  },
  messageError: {
    background: "linear-gradient(135deg, #fed7d7 0%, #fc8181 100%)",
    color: "#742a2a",
    border: "2px solid #f56565",
  },
  signupText: {
    fontSize: "12px",
    color: "#4a5568",
    margin: "16px 0 0 0",
    textAlign: "center",
    paddingTop: "16px",
    borderTop: "1px solid #e2e8f0",
  },
  link: {
    color: "#667eea",
    textDecoration: "none",
    fontWeight: "700",
    transition: "all 0.3s ease",
  },
  linkHover: {
    color: "#764ba2",
    textDecoration: "underline",
  },
};

export default Connexion;